// The customer's PO, said out loud (PO-PREDICTION.md). On a sales-order Configure whose PO was
// read, one line under the progress strip: which PO (and which of its lines), how many answers it
// filled, and how many it disagrees with the history on (the PO wins: it is this order's own
// document; the hover says what the history said). "Details" lists the disagreements and
// anything left out because this customer's POs have been wrong on it before.

import { useEffect, useState } from "react";
import { useAnswers } from "../state/AnswersContext";
import { currentFieldwise, currentPoUse, PO_READY_EVENT, type PoUse } from "../util/fieldwiseRun";

export default function PoBanner() {
  const { configMode, prediction } = useAnswers();
  const [use, setUse] = useState<PoUse | null>(null);
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(false);

  // The PO lands after the launch and grows as the later pages are predicted: read it as it changes.
  useEffect(() => {
    const read = () => setUse((u) => { const n = currentPoUse(); return n && JSON.stringify(n) !== JSON.stringify(u) ? { ...n } : u; });
    read();
    window.addEventListener(PO_READY_EVENT, read);
    const t = window.setInterval(read, 1500);
    return () => { window.removeEventListener(PO_READY_EVENT, read); window.clearInterval(t); };
  }, []);

  if (gone || !use || configMode !== "new" || !prediction?.on) return null;
  const fw = currentFieldwise();
  const say = (k: string, v: string) => (fw ? fw.labelOf(k, v) : v);
  const name = (k: string) => (fw ? fw.keyLabel(k) : k);
  const n = use.filled.length;
  const differ = use.conflicts.length;

  return (
    <div className="cpq-po-banner" role="status">
      <div className="cpq-po-banner-row">
        <span className="cpq-po-banner-text">
          Read the customer's <strong>PO {use.number}</strong>
          {use.lines > 1 ? ` (line ${use.line + 1} of ${use.lines})` : ""}: {n} answer{n === 1 ? "" : "s"} from it
          {differ ? <>, <span className="cpq-po-banner-warn">{differ} differ{differ === 1 ? "s" : ""} from this customer's history</span></> : ""}.
          {use.model ? ` The PO orders ${use.model}.` : ""}
          {use.housingDiffers.length ? <> <span className="cpq-po-banner-warn">The housing picked is not the PO's: {use.housingDiffers.map((d) => `${name(d.key)} ${say(d.key, d.chosen)} vs ${say(d.key, d.po)}`).join(", ")}.</span></> : ""}
          {use.foundBy === "text" ? " Found by its text: the file is not named for the PO number." : ""}
        </span>
        {differ || use.skipped.length ? (
          <button type="button" className="cpq-btn cpq-btn-small" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? "Hide" : "Details"}
          </button>
        ) : null}
        <button type="button" className="cpq-btn cpq-btn-small" onClick={() => setGone(true)}>Dismiss</button>
      </div>
      {open ? (
        <ul className="cpq-po-banner-list">
          {use.conflicts.map((c) => (
            <li key={c.key}>
              <strong>{name(c.key)}</strong>: PO says {say(c.key, c.po)}, history said {c.other ? say(c.key, c.other) : "nothing"}. {c.took === "history" ? "Kept the history: POs have often been wrong on this." : "Took the PO."}
            </li>
          ))}
          {use.skipped.map((s) => <li key={s}>{s}</li>)}
        </ul>
      ) : null}
    </div>
  );
}
